"use client"

import styled from "@emotion/styled"
import { Box } from "@mui/material"
import Image from "next/image"
import { extractProperty, extractPropertyMediaItems, getUmbracoMediaUrl } from "utils"
import PortContainer from "./PortContainer"
import PortTitle from "./PortTitle"

const PORT_BLUE_IMAGE_URL = "/images/port-blue-static.png"

const StyledSection = styled.section<{ $darkMode?: boolean }>`
  --p-bg: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBackgroundColor
      : props.theme.colors.port.lightBackgroundColor};
  --p-text: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkTextColor
      : props.theme.colors.port.lightTextColor};
  --p-border-color: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.lightBorderColor};
  --p-border-color-hover: ${(props) =>
    props.$darkMode
      ? props.theme.colors.port.darkBorderColor
      : props.theme.colors.port.defaultTextColor};
  --p-item-bg: ${(props) => (props.$darkMode ? "rgba(255, 255, 255, 0.05)" : "transparent")};
  --p-item-bg-hover: transparent;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 0;
  gap: 40px;
  background-color: var(--p-bg);
  color: var(--p-text);
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 120px 0;
  }

  position: relative;
  z-index: 10;
  overflow-y: visible;
  overflow-x: clip;
  &::after {
    content: url(${PORT_BLUE_IMAGE_URL});
    display: none;

    position: absolute;
    z-index: 1;
    top: -169px;
    inset-inline-end: -200px;
    transform: rotate(270deg);
    ${(props) => props.theme.breakpoints.down("md")} {
      content: "";
      transform: rotate(180deg);
      background-image: url(${PORT_BLUE_IMAGE_URL});
      background-size: contain; /* or cover */
      background-repeat: no-repeat;
      width: 126px;
      height: 128px;
      top: unset;
      bottom: -61px;
    }
  }
`

const StyledItemIcon = styled(Image)`
  object-fit: contain;
  width: 100%;
  height: 100%;
`

const StyledItemsWrapper = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: center;
  }
`

const StyledItem = styled.li`
  margin: 0;
  padding: 0;
  overflow: hidden;
  color: var(--p-text);
  border: 1px solid var(--p-border-color);
  transition: all 0.3s ease-in-out;
  background-color: var(--p-item-bg);
  &:hover {
    border-color: var(--p-border-color-hover);
    background-color: var(--p-item-bg-hover);
  }

  display: flex;
  align-items: center;
  border-radius: 20px;
  width: 100%;
  gap: 16px;
  padding: 16px;

  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: column;
    width: 242px;
    padding: 16px 16px 24px;
  }
`

const ItemTitle = styled.h4`
  margin: 0;
  padding: 0;
  font-size: 32px;
  font-weight: 700;
  line-height: 125%; /* 40px */
  text-transform: capitalize;
  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 40px;
    letter-spacing: -0.8px;
  }
`

const ItemText = styled.p`
  margin: 0;
  padding: 0;
  font-size: 16px;
  font-weight: 400;
  line-height: 150%;
  ${(props) => props.theme.breakpoints.up("md")} {
    text-align: center;
    font-size: 18px;
  }
`

type PortCustomerClubProps = {
  data: any
}

const PortCustomerClub = ({ data }: PortCustomerClubProps) => {
  const title = extractProperty(data, "title") || ""
  const subtitle = extractProperty(data, "subtitle") || ""
  const darkMode = extractProperty(data, "darkMode") || false

  const itemsList = extractProperty(data, "itemsList") || []
  const items = itemsList?.map((item: any) => ({
    title: extractProperty(item, "title"),
    icon: extractPropertyMediaItems(item, "icon")?.[0],
    text: extractProperty(item, "text"),
  }))

  // in case data fields are empty, return null
  if (!title && (!items || items.length === 0)) {
    return null
  }

  return (
    <StyledSection id="port-customer-club" $darkMode={!!darkMode}>
      <PortContainer paddingInline={16}>
        {title && (
          <PortTitle
            normalText={title}
            centered
            overwrittenColor={darkMode ? "var(--p-text)" : undefined}
            subtitle={subtitle}
          />
        )}
      </PortContainer>

      <PortContainer paddingInline={20}>
        {items && items.length > 0 && (
          <StyledItemsWrapper>
            {items?.map((item: any, index: number) => (
              <StyledItem key={index}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { md: "column" },
                    alignItems: "center",
                    gap: {
                      md: "16px",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      width: {
                        xs: "50px",
                        md: "60px",
                      },
                      height: {
                        xs: "50px",
                        md: "60px",
                      },
                    }}
                  >
                    {item.icon && (
                      <StyledItemIcon
                        src={getUmbracoMediaUrl(item.icon)}
                        alt={item.title || `icon-${index}`}
                        style={{ filter: darkMode ? "brightness(0) invert(1)" : "none" }}
                        width={60}
                        height={60}
                      />
                    )}
                  </Box>
                  {item.title && <ItemTitle>{item.title}</ItemTitle>}
                </Box>
                {item.text && <ItemText>{item.text}</ItemText>}
              </StyledItem>
            ))}
          </StyledItemsWrapper>
        )}
      </PortContainer>
    </StyledSection>
  )
}
export default PortCustomerClub
